import { useEffect, useState } from 'react';

/**
 * Returns true while the on-screen keyboard is likely visible.
 *
 * On mobile Safari + Chrome, the on-screen keyboard shrinks the visual
 * viewport without changing `window.innerHeight`. The VisualViewport API
 * exposes the actual visible height, and a meaningful gap between the
 * two is the cleanest signal that the keyboard is up.
 *
 * The 0.75 ratio is empirical: Safari's address bar collapse shrinks
 * visualViewport by ~10–15% but isn't the keyboard, while real keyboards
 * eat 35–50% of the screen. 25% is the gap that consistently separates
 * those cases without false positives.
 *
 * Used to hide the MobileTabBar and QuickActionBar while the inspector
 * is typing — otherwise the floating chrome plus the keyboard plus the
 * Safari URL bar swallow half the visible viewport.
 */
export function useKeyboardOpen(): boolean {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!vv) return;

    const update = () => {
      setOpen(vv.height < window.innerHeight * 0.75);
    };

    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    update();

    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  return open;
}
